import { BaseApiService } from './BaseApiService';

// Types
export interface InventoryAdjustment {
  id: number;
  inventoryId: number;
  adjustmentType: 'manual_add' | 'manual_subtract' | 'production_add' | 'order_deduct';
  casesBefore: number;
  unitsBefore: number;
  casesAfter: number;
  unitsAfter: number;
  casesAdjusted: number;
  unitsAdjusted: number;
  reason?: string;
  notes?: string;
  adjustedBy?: number;
  referenceId?: number;
  referenceType?: string;
  createdAt: string;
  adjustedByUser?: {
    id: number;
    username: string;
    email: string;
  };
}

export interface Inventory {
  id: number;
  productId: number;
  batchNumber?: string;
  casesAvailable: number;
  unitsAvailable: number;
  productionDate: string;
  expiryDate: string;
  notes?: string;
  productionOrderId?: number;
  createdAt: string;
  updatedAt: string;

  // Computed fields
  product?: {
    id: number;
    name: string;
    productCode: string;
    unitsPerCase: number;
    shelfLifeDays: number;
    storageRequirements: string;
    thresholdMinimumCases: number;
    thresholdBaselineCases: number;
    thresholdIdealCases: number;
  };
  isExpired?: boolean;
  daysUntilExpiry?: number;
  isExpiringSoon?: boolean;
  adjustments?: InventoryAdjustment[];
}

export interface InventorySummary {
  productId: number;
  product: {
    id: number;
    name: string;
    productCode: string;
    unitsPerCase: number;
    shelfLifeDays: number;
    storageRequirements: string;
    thresholdMinimumCases: number;
    thresholdBaselineCases: number;
    thresholdIdealCases: number;
  };
  totalCases: number;
  totalUnits: number;
  availableCases: number;
  availableUnits: number;
  expiredCases: number;
  expiredUnits: number;
  expiringSoonCases: number;
  expiringSoonUnits: number;
  belowThreshold: boolean;
  belowMinimum: boolean;
  status: 'good' | 'low' | 'critical' | 'expired';
}

export interface CreateInventoryData {
  productId: number;
  batchNumber?: string;
  casesAvailable: number;
  unitsAvailable: number;
  productionDate: string;
  expiryDate: string;
  notes?: string;
}

export interface UpdateInventoryData {
  batchNumber?: string;
  casesAvailable?: number;
  unitsAvailable?: number;
  productionDate?: string;
  expiryDate?: string;
  notes?: string;
}

export interface InventoryAdjustmentData {
  casesAdjusted: number;
  unitsAdjusted: number;
  reason?: string;
  notes?: string;
}

export interface InventoryFilters {
  productId?: number;
  productCode?: string;
  storageRequirements?: string;
  expiringSoon?: boolean;
  expired?: boolean;
  belowThreshold?: boolean;
  batchNumber?: string;
  productionDateFrom?: string;
  productionDateTo?: string;
  expiryDateFrom?: string;
  expiryDateTo?: string;
  page?: number;
  pageSize?: number;
  sortBy?: 'productName' | 'productionDate' | 'expiryDate' | 'casesAvailable';
  sortOrder?: 'asc' | 'desc';
}

export interface InventoryAlert {
  id: number;
  type: 'low_stock' | 'expired' | 'expiring_soon';
  severity: 'info' | 'warning' | 'critical';
  productId: number;
  productName: string;
  productCode: string;
  message: string;
  casesAvailable: number;
  unitsAvailable: number;
  threshold?: number;
  daysUntilExpiry?: number;
  createdAt: string;
}

export interface PaginatedInventoryResponse {
  data: Inventory[];
  pagination: {
    total: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
  };
}

export interface PaginatedInventorySummaryResponse {
  data: InventorySummary[];
  pagination: {
    total: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
  };
}

// API Service Class
class InventoryService extends BaseApiService {
  // Get inventory with filtering and pagination
  async getInventory(filters: InventoryFilters = {}): Promise<PaginatedInventoryResponse> {
    const response = await this.get<{ data: PaginatedInventoryResponse }>('/inventory', filters);
    return response.data;
  }

  // Get inventory summary by product
  async getInventorySummary(
    filters: {
      page?: number;
      pageSize?: number;
      productCode?: string;
      belowThreshold?: boolean;
    } = {}
  ): Promise<PaginatedInventorySummaryResponse> {
    const response = await this.get<{ data: PaginatedInventorySummaryResponse }>(
      '/inventory/summary',
      filters
    );
    return response.data;
  }

  // Get inventory for specific product
  async getInventoryByProduct(productId: number): Promise<Inventory[]> {
    const response = await this.get<{ data: Inventory[] }>(`/inventory/product/${productId}`);
    return response.data;
  }

  // Get specific inventory entry
  async getInventoryById(id: number): Promise<Inventory> {
    const response = await this.get<{ data: Inventory }>(`/inventory/${id}`);
    return response.data;
  }

  // Create new inventory entry
  async createInventory(data: CreateInventoryData): Promise<Inventory> {
    const response = await this.post<{ data: Inventory }>('/inventory', data);
    return response.data;
  }

  // Update inventory entry
  async updateInventory(id: number, data: UpdateInventoryData): Promise<Inventory> {
    const response = await this.put<{ data: Inventory }>(`/inventory/${id}`, data);
    return response.data;
  }

  // Delete inventory entry
  async deleteInventory(id: number): Promise<void> {
    await this.delete(`/inventory/${id}`);
  }

  // Adjust inventory manually
  async adjustInventory(
    id: number,
    adjustment: InventoryAdjustmentData
  ): Promise<{
    inventory: Inventory;
    adjustment: any;
  }> {
    const response = await this.post<{ data: { inventory: Inventory; adjustment: any } }>(
      `/inventory/${id}/adjust`,
      adjustment
    );
    return response.data;
  }

  // Get inventory alerts
  async getInventoryAlerts(): Promise<InventoryAlert[]> {
    const response = await this.get<{ data: InventoryAlert[] }>('/inventory/alerts');
    return response.data;
  }

  // Helper methods for common operations
  async getLowStockProducts(): Promise<InventorySummary[]> {
    const response = await this.getInventorySummary({ belowThreshold: true });
    return response.data.filter((item) => item.belowThreshold);
  }

  async getExpiringProducts(daysAhead: number = 7): Promise<Inventory[]> {
    const response = await this.getInventory({ expiringSoon: true });
    return response.data.filter(
      (item) =>
        item.daysUntilExpiry !== undefined &&
        item.daysUntilExpiry <= daysAhead &&
        item.daysUntilExpiry >= 0
    );
  }

  async getExpiredProducts(): Promise<Inventory[]> {
    const response = await this.getInventory({ expired: true });
    return response.data.filter((item) => item.isExpired);
  }

  // Format helpers
  formatExpiryStatus(inventory: Inventory): {
    status: 'good' | 'warning' | 'expired';
    message: string;
    color: string;
  } {
    if (inventory.isExpired) {
      return {
        status: 'expired',
        message: 'Expired',
        color: 'error',
      };
    }

    if (inventory.isExpiringSoon) {
      return {
        status: 'warning',
        message: `Expires in ${inventory.daysUntilExpiry} days`,
        color: 'warning',
      };
    }

    return {
      status: 'good',
      message: `${inventory.daysUntilExpiry} days remaining`,
      color: 'success',
    };
  }

  formatStockStatus(summary: InventorySummary): {
    status: 'good' | 'low' | 'critical' | 'out';
    message: string;
    color: string;
  } {
    if (summary.availableCases === 0) {
      return {
        status: 'out',
        message: 'Out of stock',
        color: 'error',
      };
    }

    if (summary.belowMinimum) {
      return {
        status: 'critical',
        message: 'Critical low stock',
        color: 'error',
      };
    }

    if (summary.belowThreshold) {
      return {
        status: 'low',
        message: 'Low stock',
        color: 'warning',
      };
    }

    return {
      status: 'good',
      message: 'In stock',
      color: 'success',
    };
  }
}

// Export singleton instance
export const inventoryService = new InventoryService();
export default inventoryService;
